import { useMemo } from "preact/hooks";
import classnames from "classnames";
import Icons from "@components/icons.ts";
import { ComboboxButtonWrapper } from "@components/ComboBoxes/ComboboxButtonWrapper";
import type { State, Payor } from "@services/filter-api/types";

interface StateFilterButtonProps {
  breakpoint?: string;
  centeredFilters?: boolean;
  combinedSelectedItems: {
    state: State | null;
    insurance: Payor | null;
    specialty: any;
  };
  error?: string;
  filter_buttons_props?: Array<{
    name: string;
    label: string;
    is_optional: boolean;
    is_multi_select: boolean;
    placeholder: string;
  }>;
  handleGetSelectedItem: (item: any) => void;
  handleOpenClick: (open: boolean) => void;
  insurance: Payor[];
  isMobile?: boolean;
  new_styles?: boolean;
  open: boolean;
  optional?: boolean;
  rebrand?: boolean;
  selectedItem: State | null;
  statesFilters: State[];
  tabbedFilters?: boolean;
}

export const StateFilterButton = ({
  breakpoint,
  centeredFilters,
  combinedSelectedItems,
  error,
  filter_buttons_props,
  handleGetSelectedItem,
  handleOpenClick,
  insurance,
  isMobile,
  new_styles,
  open,
  optional,
  rebrand,
  selectedItem,
  statesFilters,
  tabbedFilters,
}: StateFilterButtonProps) => {
  const filteredStates = useMemo(() => {
    if (!combinedSelectedItems.insurance) {
      return statesFilters;
    }

    const selectedInsuranceValue = combinedSelectedItems.insurance.value;
    if (!selectedInsuranceValue) {
      return statesFilters;
    }

    const selectedInsurance = insurance.find(
      (_insurance) => _insurance.value === selectedInsuranceValue
    );

    if (!selectedInsurance?.states) {
      return statesFilters;
    }

    return statesFilters.map((_state) => {
      const hasStates = selectedInsurance.states?.find(
        (_s) => _s.abbreviation?.toLowerCase() === _state.abbreviation?.toLowerCase()
      );
      
      return hasStates 
        ? { ..._state, filtered: true }
        : _state;
    });
  }, [statesFilters, insurance, combinedSelectedItems.insurance]);

  const buttonWrapperClass = useMemo(() => {
    return classnames(
      "w-11/12 md:rounded-s-lg border-b md:border my-2 md:my-0",
      {
        "border-[#B35436] border-error": error,
        "border-gt-black": !error,
      }
    );
  }, [error]);

  const placeholder = useMemo(() => {
    return filter_buttons_props?.[0]?.placeholder || "Select your state";
  }, [filter_buttons_props]);

  const label = useMemo(() => {
    const buttonProp = filter_buttons_props?.[0];
    if (buttonProp) {
      return `${buttonProp.label}${buttonProp.is_optional ? "" : "*"}`;
    }
    return "location";
  }, [filter_buttons_props]);

  const endIcon = useMemo(() => {
    return <Icons.CaretDownIcon open={open} />;
  }, [open]);

  return (
    <ComboboxButtonWrapper
      breakpoint={breakpoint}
      buttonWrapperClass={buttonWrapperClass}
      centeredFilters={centeredFilters}
      data={filteredStates}
      endIcon={endIcon}
      error={error}
      handleGetSelectedItem={handleGetSelectedItem}
      handleOpenClick={handleOpenClick}
      isMobile={isMobile}
      label={label}
      name="State"
      new_styles={new_styles}
      open={open}
      optional={optional}
      placeholder={placeholder}
      rebrand={rebrand}
      selectedItem={selectedItem}
      tabbedFilters={tabbedFilters}
    />
  );
};
